/**
 * Punch-level attendance arithmetic — the one place that turns raw
 * `/me/attendance` rows into sessions, breaks, and one day's figures.
 *
 * WHERE A DAY'S FIGURES COME FROM. Every `/me/attendance*` row carries ERPat's
 * OWN `metrics` block beside its punches (HumanResource 1.38.0 — the same
 * projection the manager-level `/v1/api/attendance` returns), computed by the
 * rule the web Metrics modal uses (`Attendance::log_details()` →
 * `AttendancesTrait::_row_metrics_only()`): the STORED payroll row for an
 * approved record, a LIVE `BioMeet` run for any other record with a clock-out,
 * and nothing for a punch still open. Those figures know the tenant's rules —
 * force-8h, rounding increments, grace, lunch, night differential — and they
 * WIN. `readRowMetrics` reads the block and its `metrics_source`
 * (`stored` | `computed` | `none`); `rollupDayMetrics` sums the day's rows.
 *
 * The device measures only what the server has not: a punch with no metrics
 * (a shift still open, or an older backend), and the day's BREAK total — the
 * server reports overbreak (`over`) but never the break time itself. So the
 * arithmetic below is still the Home widgets' arithmetic, applied per session
 * only where `PunchSession.metrics` is null. Every figure says which it is
 * ({@link DayFigureVM.source}), so a surface can label a device estimate as
 * one and never dress it up as ERPat's number.
 *
 * ⚠ A `computed` figure is a PREVIEW of what approval would store, and any
 * overtime in it is measured, not payable, until the review rules on it —
 * authoritative pay numbers come only from a payslip.
 *
 * Extracted out of `services/widgets.ts` so the Timesheet cut-off card can read
 * a day exactly the way the Home widgets read today. A second implementation
 * would eventually disagree with the first about the same punches.
 */
import { asRecord, pick, type Json } from '@/services/api/http';
import { localDateKey } from '@/utils/format';
import {
  readRowMetrics,
  type AttendanceMetricsVM,
  type AttendanceOtReviewState,
  type MetricsSource,
} from '@/services/api/attendance-metrics';

const str = (rec: Json, keys: string[], fallback = ''): string => {
  const v = pick<unknown>(rec, keys, fallback);
  return v == null ? fallback : String(v);
};

/**
 * Tardiness beyond this in one day reads as a missed half-day, not lateness —
 * and so does leaving more than this before the scheduled end. See
 * {@link tallyWorkday}.
 *
 * A DEVICE rule: it shapes only the figures measured here from the punches. A
 * row that carries ERPat's `metrics` reports the engine's own `late` / `under`,
 * which apply the tenant's grace and rounding instead of this cap.
 */
export const MAX_TARDY_MINUTES_PER_DAY = 240;

/**
 * The break allowance a DEVICE-measured overbreak is judged against — anything
 * past it is overbreak.
 *
 * It is a CLIENT-SIDE constant because no end-user endpoint exposes the tenant's
 * configured allowance: `/me/schedule` answers `in_time` / `out_time` /
 * `is_workday` per weekday and nothing else. It matters only for a day with no
 * server figures (a shift still open, an older backend): a row with `metrics`
 * carries ERPat's own `over`, measured against the tenant's real allowance.
 * Surfaces that show a device estimate name the allowance in their copy rather
 * than letting the number look like a server-computed one.
 */
export const NORMAL_BREAK_MINUTES = 90;

// ── Clock-string helpers ─────────────────────────────────────────────────────
/**
 * Parse a schedule clock string — "08:30", "17:05:00", or "8:30 AM" — into
 * minutes after local midnight. Returns null for anything unparseable.
 */
export function clockToMinutes(value: string): number | null {
  const m = value.trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i);
  if (!m) return null;
  let hours = Number(m[1]);
  const minutes = Number(m[2]);
  const meridian = m[3]?.toUpperCase();
  if (meridian === 'PM' && hours < 12) hours += 12;
  if (meridian === 'AM' && hours === 12) hours = 0;
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/** Minutes-after-midnight → "8:30 AM" display label. */
export function minutesToLabel(minutes: number): string {
  const h24 = Math.floor(minutes / 60) % 24;
  const m = Math.round(minutes % 60);
  const meridian = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${meridian}`;
}

/** Compact duration from minutes: 214 → "3h 34m", 58 → "58m", 360 → "6h". */
export function compactDur(minutes: number): string {
  const total = Math.max(0, Math.round(minutes));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h <= 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/**
 * Split a tenant-local "Y-m-d H:i:s" stamp into its calendar date and
 * minutes-of-day WITHOUT running it through `Date` (which would reinterpret
 * the wall-clock string in the device zone).
 *
 * `seconds` is the same instant as seconds-of-day, the stamp's `:SS` included
 * (0 when it carries none) — what a running timer measures an open punch from.
 * `minutes` drops the seconds, as every punch-card label always has.
 */
export function splitLocalStamp(stamp: string): { dateKey: string; minutes: number; seconds: number } | null {
  const m = stamp.trim().match(/^(\d{4}-\d{2}-\d{2})[T ](\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return null;
  const minutes = Number(m[2]) * 60 + Number(m[3]);
  return { dateKey: m[1]!, minutes, seconds: minutes * 60 + Number(m[4] ?? 0) };
}

/** Seconds after local midnight at `at` (device clock ≈ employee's zone, as {@link nowMinutes}). */
function secondsOfDay(at: Date): number {
  return at.getHours() * 3600 + at.getMinutes() * 60 + at.getSeconds();
}

/**
 * Whole days from `fromKey` to `toKey`, both `YYYY-MM-DD`. Built by stepping
 * LOCAL dates (never a ms subtraction), so a DST transition inside the span
 * cannot round the answer to 0 or 2. Null when either key is unparseable.
 */
function dayGap(fromKey: string, toKey: string): number | null {
  const at = (key: string): Date | null => {
    const m = key.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12) : null;
  };
  const from = at(fromKey);
  const to = at(toKey);
  if (!from || !to) return null;
  return Math.round((to.getTime() - from.getTime()) / 86_400_000);
}

/** Minutes after local midnight right now (device clock ≈ employee's zone). */
export function nowMinutes(): number {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
}

// ── Attendance sessions ──────────────────────────────────────────────────────
export interface PunchSession {
  /**
   * The day the punch is FILED under — ERPat's `metrics.log_date` when the row
   * carries one (see {@link rowDayKey}), else the punch-in's local date. This is
   * the key `groupSessionsByDay` groups by, so a night shift lands on the day
   * the web Summary tab and `/me/attendance/summary` file it under.
   */
  dateKey: string;
  /** The punch-in's own local calendar date — the frame every minute below is anchored at. */
  inDateKey: string;
  /** The punch-out's local calendar date; '' while the punch is open. */
  outDateKey: string;
  /** The row `id` ('' when the server sent none). */
  id: string;
  /** The row's own `status` (`incomplete` | `pending` | `approved` | `rejected` | `clockout` | ''). */
  status: string;
  /**
   * ERPat's figures for THIS punch — null when the server has none (an open
   * punch, or an older backend), in which case the day's arithmetic measures the
   * punch on the device. See {@link readRowMetrics} for the three states.
   */
  metrics: AttendanceMetricsVM | null;
  metricsSource: MetricsSource;
  inMinutes: number;
  /**
   * The punch-in to the SECOND, after the same midnight as {@link inMinutes}.
   * Only an OPEN punch is measured from it (a running timer must not lose up to
   * 59 s to the minute floor); a closed one is measured in whole minutes, so a
   * finished day reads exactly as it always has.
   */
  inSeconds: number;
  /**
   * The punch-out CLAMPED to the punch-in's own day: the raw minutes when both
   * stamps share a date, a flat `24 * 60` when the shift ran past midnight, and
   * null while the punch is still open. Kept for the same-day punch-card model
   * the Home widgets are built on — use {@link outAbsolute} to measure a span.
   */
  outMinutes: number | null;
  /**
   * The punch-out in a MONOTONIC frame anchored at the punch-in day's midnight,
   * so a 5:40 AM clock-out on the next day reads 1780, not 340 — which is what
   * makes an overnight span, its overtime, and its displayed clock time correct
   * rather than clamped to midnight.
   *
   * Null while the punch is open, and ALSO null when the out stamp lands more
   * than one day after the in stamp: that is a stuck record, not a 30-hour
   * shift, and a span measured off it would be fiction.
   */
  outAbsolute: number | null;
  /**
   * Break taps logged on this punch, chronological — one entry per tap
   * (0 = first break-out, 1 = first break-in, 2 = second break-out, …), in
   * minutes after the punch-in day's midnight. `null` marks a tap the server
   * sent with an unparseable stamp: the slot is KEPT so the out/in alternation
   * (and the odd-count "still on break" reading) can never desync.
   */
  breaks: (number | null)[];
  /**
   * The same taps to the SECOND — one slot per entry in {@link breaks}, same
   * order, same next-day carry, same nulls. Keep the two arrays in step: any
   * code that replaces `breaks` replaces this too.
   */
  breakSeconds: (number | null)[];
  /** The row's own `note` — the employee's remark on that punch ('' when none). */
  note: string;
}

/**
 * Break taps off one `/me/attendance` row. The end-user API projects
 * `break_time` as `{ utc, local }` objects through `Attendance_euapi::shape()`
 * — on EVERY row, not just the open one — so a closed shift still reports the
 * breaks taken inside it. Older/absent payloads degrade to `[]`.
 *
 * `dayKey` is the punch-in's local date: a tap on a later calendar day (an
 * overnight shift's 1 AM break) is carried past midnight as `+24h` so ordering
 * and duration math stay monotonic; `minutesToLabel` wraps it back for display.
 */
function parseBreakTaps(row: Json, dayKey: string): { minutes: (number | null)[]; seconds: (number | null)[] } {
  const raw = pick<unknown>(row, ['break_time', 'breaks'], []);
  if (!Array.isArray(raw)) return { minutes: [], seconds: [] };
  const taps = raw.flatMap((entry): Array<[number, number] | null> => {
    const stamp = typeof entry === 'string' ? entry : str(asRecord(entry), ['local', 'utc', 'time']);
    const parts = splitLocalStamp(stamp);
    if (!parts) return [null];
    const gap = dayGap(dayKey, parts.dateKey);
    // Same day, or the next one — an overnight shift's 1 AM break. Carried past
    // midnight so ordering and duration math stay monotonic.
    if (gap === 0) return [[parts.minutes, parts.seconds]];
    if (gap === 1) return [[parts.minutes + 24 * 60, parts.seconds + 86_400]];
    // ANYTHING ELSE IS DROPPED, and dropped rather than carried further, because
    // the server appends new taps to whichever shift it still considers open. A
    // punch nobody clocked out of therefore keeps collecting taps from days that
    // have nothing to do with it — today's break landing on a shift from two
    // days ago. Attributing those to this day invented hours: a 22-minute lunch
    // and a 4h 13m "break" measured between two unrelated calendar days.
    //
    // The slot is not preserved. A tap belonging to another day is not a missing
    // half of this day's pair, so keeping a null for it would break the very
    // out/in alternation the null is there to protect.
    return [];
  });
  return { minutes: taps.map((t) => (t ? t[0] : null)), seconds: taps.map((t) => (t ? t[1] : null)) };
}

const DATE_KEY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The local calendar date a row's punch-in fell on, `YYYY-MM-DD`, read off the
 * same stamps the session parser and the week strip read; '' when none parses.
 */
function punchInDateKey(r: Json): string {
  const parts = splitLocalStamp(str(r, ['in_time_local', 'in_time']));
  if (parts) return parts.dateKey;
  const fallback = str(r, ['date', 'date_created', 'in_time_utc'], '').slice(0, 10);
  return DATE_KEY_RE.test(fallback) ? fallback : '';
}

/**
 * The day a punch is FILED under: ERPat's `metrics.log_date` when the row
 * carries figures and that date is within a day of the punch-in, else the
 * punch-in's own local date.
 *
 * `log_date` is the schedule day the engine matched the punch to — the day the
 * web Summary tab, `/me/attendance/summary` and payroll all file it under — and
 * for a night shift it can be the day BEFORE or AFTER the clock-in. Reading it
 * keeps the day's status (`punchDaysOf`) and its sessions on the same row of the
 * cut-off. Anything further than ±1 day is not a plausible schedule match, so the
 * punch-in date stands. A row with no figures (an open punch) has nothing to
 * file it elsewhere and always lands on its punch-in date.
 */
export function rowDayKey(row: Json): string {
  const r = asRecord(row);
  return filedDayKey(punchInDateKey(r), readRowMetrics(r).metrics);
}

function filedDayKey(inDateKey: string, metrics: AttendanceMetricsVM | null): string {
  if (!inDateKey || !metrics) return inDateKey;
  const logKey = metrics.logDate.slice(0, 10);
  if (!DATE_KEY_RE.test(logKey)) return inDateKey;
  const gap = dayGap(inDateKey, logKey);
  return gap != null && Math.abs(gap) <= 1 ? logKey : inDateKey;
}

/**
 * Group raw `/me/attendance` rows into per-day punch sessions, keyed by the day
 * each punch is FILED under ({@link rowDayKey}) and sorted by clock-in time.
 * Rows without a parseable local in-time are dropped.
 */
export function groupSessionsByDay(rows: Json[]): Map<string, PunchSession[]> {
  const byDay = new Map<string, PunchSession[]>();
  for (const row of rows) {
    const r = asRecord(row);
    const inParts = splitLocalStamp(str(r, ['in_time_local', 'in_time']));
    if (!inParts) continue;
    const outParts = splitLocalStamp(str(r, ['out_time_local', 'out_time']));
    const spanDays = outParts ? dayGap(inParts.dateKey, outParts.dateKey) : null;
    const rowMetrics = readRowMetrics(r);
    const taps = parseBreakTaps(r, inParts.dateKey);
    const session: PunchSession = {
      dateKey: filedDayKey(inParts.dateKey, rowMetrics.metrics),
      inDateKey: inParts.dateKey,
      outDateKey: outParts?.dateKey ?? '',
      id: str(r, ['id', 'uuid'], ''),
      status: str(r, ['status'], '').toLowerCase().trim(),
      metrics: rowMetrics.metrics,
      metricsSource: rowMetrics.source,
      inMinutes: inParts.minutes,
      inSeconds: inParts.seconds,
      // An overnight clock-out belongs to the punch-in's day; keep the raw
      // minutes only when the out stamp shares the same calendar date.
      outMinutes: outParts && outParts.dateKey === inParts.dateKey ? outParts.minutes : outParts ? 24 * 60 : null,
      outAbsolute:
        outParts && spanDays != null && spanDays >= 0 && spanDays <= 1
          ? outParts.minutes + spanDays * 24 * 60
          : null,
      breaks: taps.minutes,
      breakSeconds: taps.seconds,
      note: str(r, ['note'], '').trim(),
    };
    const list = byDay.get(session.dateKey) ?? [];
    list.push(session);
    byDay.set(session.dateKey, list);
  }
  for (const list of byDay.values()) list.sort((a, b) => filedInMinutes(a) - filedInMinutes(b));
  return byDay;
}

/**
 * The punch-in in the FILED day's frame — `inMinutes` is anchored at the
 * punch-in's own midnight, so a punch filed under the next day (a night shift
 * ERPat logs on the morning it ends) is carried back a day to sort before that
 * day's own punches, and one filed under the previous day is carried forward.
 * Ordering only: every measured minute stays in the punch-in frame.
 */
function filedInMinutes(session: PunchSession): number {
  const gap = session.dateKey === session.inDateKey ? 0 : (dayGap(session.dateKey, session.inDateKey) ?? 0);
  return session.inMinutes + gap * 24 * 60;
}

/**
 * The sessions whose punch-IN fell on `dayKey` (local), in clock-in order —
 * the selection every TODAY surface reads, as opposed to the FILED day
 * `groupSessionsByDay` keys by.
 *
 * "Today" is the day the employee clocked in, not the schedule day ERPat's
 * engine filed the record under: a night shift punched in this evening is
 * today's shift even though `metrics.log_date` may file it under tomorrow, and
 * a record punched in yesterday that the engine filed under today is
 * yesterday's — reading the filed key made the first vanish from the Home
 * card the moment it was clocked out, and let the second stand in for a day
 * the employee had not started yet.
 */
export function sessionsPunchedInOn(rows: Json[], dayKey: string): PunchSession[] {
  const sessions = [...groupSessionsByDay(rows).values()].flat().filter((s) => s.inDateKey === dayKey);
  return sessions.sort((a, b) => a.inMinutes - b.inMinutes);
}

/**
 * The punch sessions a "today" derivation should measure, reconciled across the
 * TWO endpoints that describe the same shift. They disagree, each in its own
 * direction:
 *
 *  - `/me/attendance?start_date=…&end_date=…` returns only rows whose punch-IN
 *    falls inside the window, so a shift that began last night and is still
 *    running is simply absent from today's window — while `/me/attendance/today`
 *    hands that very row to the Recent Activity list and the Take a Break
 *    button. Reading the window alone reported "not clocked in" for a shift the
 *    rest of the page was showing as live.
 *
 *  - The open record has always been a dependable source of `break_time`. Every
 *    listed row projects it too (`Attendance_euapi::shape()`, and the
 *    `EuapiAttendanceRecord` schema documents it since HumanResource 1.38.0),
 *    but an older backend's list did not — reading the listed row alone showed
 *    0h 00m against a break pair plainly visible in Recent Activity.
 *
 * So: prefer the listed rows, which cover the whole day including closed
 * punches AND carry ERPat's `metrics` for a closed one (the open record never
 * does — `today()` reads the raw clock-in row), and overlay the open record's
 * break taps onto the row that IS that punch. Whichever side actually carries
 * them, the day totals the same.
 *
 * "Today" is the day the punch-in happened ({@link sessionsPunchedInOn}), never
 * the day ERPat's `log_date` filed the record under.
 *
 * The open-record fallback is bounded to one day, the same rule
 * {@link PunchSession.outAbsolute} applies: a punch still open from several days
 * ago is a record nobody clocked out of, not a multi-day shift, and measuring
 * today's schedule against it would invent a lateness that never happened.
 */
export function reconcileTodaySessions(
  listedRows: Json[],
  openRecord: Json,
  todayKey: string,
): PunchSession[] {
  const tracked = [...groupSessionsByDay([openRecord]).values()].flat()[0];
  const listed = sessionsPunchedInOn(listedRows, todayKey);

  // OVERLAY — no openness test. `open_record` keeps describing the punch after
  // the employee clocks out (that is where the Time Out row's own label comes
  // from), it just gains an out-time. Requiring it to be open dropped the day's
  // break taps at the moment of clock-out: break time read correctly all shift
  // and then fell back to 0h 00m on time-out. Same punch-in day + same clock-in
  // minute is already proof it is the same punch, so the taps are valid either
  // way. Matched on `inDateKey`, not `dateKey`: the listed row may be FILED under
  // ERPat's log date while `open_record` — served raw, with no metrics — can
  // only be filed under its punch-in date.
  //
  // ONLY the taps cross over. The listed row's `metrics` stay: `open_record`
  // never carries figures (`metrics_source: none` — `today()` reads the raw
  // clock-in row), so copying it wholesale would erase the `computed` figures
  // the list gained the moment the shift was clocked out.
  if (listed.length) {
    if (!tracked) return listed;
    // Take the taps from whichever side reported more, so a listed row that
    // already carries them is untouched and this can only ADD breaks really
    // logged.
    return listed.map((s) =>
      s.inDateKey === tracked.inDateKey &&
      s.inMinutes === tracked.inMinutes &&
      tracked.breaks.length > s.breaks.length
        ? { ...s, breaks: tracked.breaks, breakSeconds: tracked.breakSeconds }
        : s,
    );
  }

  // FALLBACK — nothing in today's window, so the tracked punch has to stand in
  // for the day. THIS is where openness matters: only a running shift belongs to
  // today, and only within a day of it. A punch still open from further back is
  // a record nobody clocked out of, not a multi-day shift, and measuring today's
  // schedule against it would invent a lateness that never happened.
  if (!tracked || tracked.outMinutes != null) return [];
  const gap = dayGap(tracked.inDateKey, todayKey);
  return gap != null && gap >= 0 && gap <= 1 ? [tracked] : [];
}

// ── Breaks ───────────────────────────────────────────────────────────────────
/** One break interval: when the employee stepped out, and when they came back. */
export interface BreakInterval {
  /** Break-out, minutes after midnight; null when the server stamp was unreadable. */
  out: number | null;
  /** Break-in; null when the employee never logged a return. */
  in: number | null;
  /**
   * Where the break stops accruing time: the break-in, or — when the return was
   * never tapped — the punch's own clock-out, since clocking out necessarily
   * ended it. Null ONLY while the break is genuinely still running on an open
   * punch; without this an unclosed break would keep counting all evening.
   */
  endsAt: number | null;
  /**
   * 'tap' — the Take a Break button, logged inside one punch.
   * 'gap' — a split shift, where the break IS the space between two punches.
   * Both count toward the day's break total, and either can fill the Home punch
   * card's Break Out / Break In cells — the card has no AM Out / PM In, so a
   * lunch logged by clocking out and back in shows there or nowhere.
   */
  source: 'tap' | 'gap';
}

/**
 * Every break the employee took on one day, chronological.
 *
 * ERPat records a lunch break TWO ways and a tenant may use either, so both are
 * collected — they can never overlap (a tap lives inside one punch, a gap lives
 * between two), so the union is the day's real break time:
 *   1. Break taps on a punch (the Take a Break / Back to Work button) —
 *      `break_time`, alternating out/in. Projected on EVERY row, so a closed
 *      shift still reports the breaks taken inside it.
 *   2. A split shift: clocking OUT for lunch and back IN, which leaves the same
 *      break as the gap between two punches.
 */
export function collectBreaks(sessions: PunchSession[]): BreakInterval[] {
  const intervals: BreakInterval[] = [];

  for (const session of sessions) {
    for (let i = 0; i < session.breaks.length; i += 2) {
      // A trailing lone tap is an unreturned break, not a malformed pair.
      const returnTap = i + 1 < session.breaks.length ? (session.breaks[i + 1] ?? null) : null;
      const out = session.breaks[i] ?? null;
      intervals.push({ out, in: returnTap, endsAt: returnTap ?? session.outMinutes, source: 'tap' });
    }
  }

  for (let i = 1; i < sessions.length; i += 1) {
    const prevOut = sessions[i - 1]?.outMinutes ?? null;
    const nextIn = sessions[i]?.inMinutes ?? null;
    if (prevOut != null && nextIn != null && nextIn > prevOut) {
      intervals.push({ out: prevOut, in: nextIn, endsAt: nextIn, source: 'gap' });
    }
  }

  // Order by when the break started, falling back to when it ended so a break
  // whose out-stamp was unreadable still lands in its real place in the day.
  const at = (b: BreakInterval): number => b.out ?? b.in ?? Number.POSITIVE_INFINITY;
  return intervals.sort((a, b) => at(a) - at(b));
}

// ── One day, measured ────────────────────────────────────────────────────────
/** The shift window a day is measured against, in minutes after local midnight. */
export interface DayShift {
  isWorkday: boolean;
  inMinutes: number | null;
  outMinutes: number | null;
}

/** One PUNCHED workday as the pay-period insights count it. */
export interface WorkdayTally {
  /** 0, 0.5 (one half of the shift missed) or 1 (both halves missed). */
  absentDays: number;
  /** Lateness in minutes — 0 when the first half was missed outright. */
  tardyMinutes: number;
  /**
   * UNDERTIME — minutes the day ended early, i.e. the last clock-out landing
   * before the scheduled end. The mirror image of {@link tardyMinutes}, and the
   * same thing ERPat's own engine means by it (`BioMeet`:
   * `under = sched_out - time_out`, positive only) — early departure, not
   * "scheduled hours not worked", so a late start is never counted twice.
   *
   * 0 when the second half was missed outright (that is the half-day absence
   * above, not undertime), and 0 until the day is over with a real clock-out:
   * while the employee may still clock back in, an open punch says nothing about
   * when they left.
   */
  undertimeMinutes: number;
}

/**
 * The sessions a day's FIGURES are measured from: every punch but a
 * `rejected` one. A rejected record is not attendance — ERPat's own summary
 * skips it, its web Summary tab does not sum it, payroll never sees it — so it
 * contributes nothing to any total here, however it is listed elsewhere.
 */
export function countedSessions(sessions: PunchSession[]): PunchSession[] {
  return sessions.filter((s) => s.status !== 'rejected');
}

/**
 * Tally one workday that carries at least one punch, half-days included.
 *
 * A half of the shift counts as absent (0.5) when the day is short by more than
 * {@link MAX_TARDY_MINUTES_PER_DAY} at that end:
 *  - FIRST half — the first clock-in lands more than that after the scheduled
 *    start. The lateness IS the half-day, so it is not also counted as tardiness.
 *  - SECOND half — the last clock-out lands more than that before the scheduled
 *    end. Only judged once the day is over and its last punch has a real
 *    clock-out: until then the employee may still clock back in, and an open or
 *    unresolved punch says nothing about when they left.
 * Both halves missed is a whole day.
 *
 * A `rejected` row is not attendance ({@link countedSessions}) and is judged
 * from neither end.
 */
export function tallyWorkday(allSessions: PunchSession[], shift: DayShift, dayIsOver: boolean): WorkdayTally {
  const sessions = countedSessions(allSessions);
  const first = sessions[0];
  const last = sessions[sessions.length - 1];
  if (!shift.isWorkday || shift.inMinutes == null || !first || !last) {
    return { absentDays: 0, tardyMinutes: 0, undertimeMinutes: 0 };
  }

  let absentDays = 0;
  let tardyMinutes = 0;
  let undertimeMinutes = 0;

  const late = first.inMinutes - shift.inMinutes;
  if (late > MAX_TARDY_MINUTES_PER_DAY) absentDays += 0.5;
  else if (late > 0) tardyMinutes = late;

  if (dayIsOver && shift.outMinutes != null && last.outAbsolute != null) {
    // `outAbsolute` is anchored at the punch-in day's midnight, so an overnight
    // shift's end is carried past midnight into the same frame.
    const shiftOut = shift.outMinutes <= shift.inMinutes ? shift.outMinutes + 24 * 60 : shift.outMinutes;
    const early = shiftOut - last.outAbsolute;
    // Past the half-day threshold the missed half IS the absence, so it is not
    // also reported as undertime — the same either/or the lateness branch applies.
    if (early > MAX_TARDY_MINUTES_PER_DAY) absentDays += 0.5;
    else if (early > 0) undertimeMinutes = early;
  }

  return { absentDays, tardyMinutes, undertimeMinutes };
}

/** One break as the day sheet lists it. */
export interface DayBreakVM {
  /** "12:00 PM" — '' when the server sent an unreadable stamp. */
  outLabel: string;
  /** "1:02 PM" — '' when the return was never logged. */
  inLabel: string;
  /** "1h 02m"; '' while the break is still running. */
  durationLabel: string;
  /** True while the employee has not tapped back in. */
  open: boolean;
  /** How it was recorded — a break tap, or the gap of a split shift. */
  source: 'tap' | 'gap';
}

/**
 * Where one figure came from:
 *   stored   — ERPat's stored payroll row (approved attendance);
 *   computed — ERPat's live engine preview of a clocked-out, not-yet-approved
 *              record (what approval would store);
 *   summary  — `/me/attendance/summary`'s own per-day figure, measured by the
 *              server's calculator from punches (grace applied) on a day it has
 *              no metrics for;
 *   device   — measured on this device from the punches and the schedule;
 *   mixed    — a sum of server figures and a device-measured punch on the same
 *              day (a closed row beside a shift still open).
 */
export type FigureSource = 'stored' | 'computed' | 'summary' | 'device' | 'mixed';

/** One figure on the day sheet. `warn` is a semantic status, not decoration. */
export interface DayFigureVM {
  key: 'worked' | 'late' | 'overtime' | 'break' | 'overbreak' | 'undertime' | 'nightdiff';
  label: string;
  value: string;
  minutes: number;
  warn: boolean;
  /** Provenance — what a surface labels the figure by. */
  source: FigureSource;
  /**
   * A short qualifier the surface prints beside the value ('' when none) — the
   * overtime figure's 'awaiting approval' / 'under review' / 'not payable'.
   */
  hint: string;
}

export interface AttendanceDayDetailVM {
  /** True when the day carries at least one punch. */
  hasPunch: boolean;
  /** First clock-in, e.g. "8:02 AM" ('' when the day was never punched). */
  timeInLabel: string;
  /** Last clock-out; '' while the final punch is still open. */
  timeOutLabel: string;
  /** True while the final punch has no clock-out. */
  stillClockedIn: boolean;
  /**
   * True when the day's last punch clocked out on the calendar day AFTER the
   * one it is filed under (a night shift filed on the evening it started).
   */
  endedNextDay: boolean;
  /**
   * True when the day's first punch clocked in on the calendar day BEFORE the
   * one it is filed under (a night shift ERPat filed on the morning it ended).
   */
  startedPreviousDay: boolean;
  /** The scheduled window, e.g. "8:00 AM – 5:00 PM" ('' on a rest day). */
  shiftLabel: string;
  /** How many punch pairs make up the day — 2+ is a split shift. */
  sessionCount: number;
  /**
   * Where the day's figures came from as a whole: `stored` / `computed` when
   * every punch carries ERPat's figures, `mixed` when a device-measured punch
   * sits beside them, `device` when none does. Per-figure detail is on each
   * {@link DayFigureVM.source} — the break total is always `device`.
   */
  metricsSource: FigureSource;
  /** ERPat's log date for the day's figures (YYYY-MM-DD); '' when the device measured everything. */
  logDate: string;
  /** The overtime figure's qualifier, repeated for the sheet's copy ('' when none). */
  otHint: string;
  /**
   * Worked / Late / Overtime / Break / Overbreak, in that reading order, then
   * Night diff. on a day with ERPat's figures — plus Undertime (inserted before
   * Night diff.) on a payroll cut-off day (`mapCutoffPeriod`).
   */
  figures: DayFigureVM[];
  breaks: DayBreakVM[];
  /** Every note written on the day's punches, in punch order. */
  notes: string[];
  /**
   * True when a DEVICE-measured punch carries a clock-out the server left more
   * than a day after its clock-in. The day's hours can't be measured off a
   * record like that, so the figures read "—" rather than asserting a 30-hour
   * shift. A punch ERPat has figures for is exempt — the engine already read it.
   */
  punchOutUnresolved: boolean;
  /**
   * What the shift is doing right now — `working` / `on_break` while TODAY's
   * last punch is open, null otherwise (clocked out, never in, or a past day).
   * What a running timer ticks on.
   */
  liveState: DayLiveState | null;
  /**
   * The worked / break / overtime figures to the SECOND, for a running timer —
   * the same totals the figures carry in minutes (`minutes` is these floored).
   * Exact for an open punch; ERPat's figures and closed punches contribute
   * whole minutes.
   */
  seconds: DayLiveSeconds;
}

/** What an open shift is doing: on the clock, or on a break (worked time paused). */
export type DayLiveState = 'working' | 'on_break';

/** The day's running totals in seconds — see {@link AttendanceDayDetailVM.seconds}. */
export interface DayLiveSeconds {
  worked: number;
  break: number;
  overtime: number;
}

// ── ERPat's figures, rolled up per day ───────────────────────────────────────
/**
 * One day's ERPat figures, summed over every punch that carries `metrics`, in
 * MINUTES (the engine's decimal hours × 60, rounded per row — the same rounding
 * `/me/attendance/summary` applies before it sums a date).
 */
export interface DayMetricsRollup {
  /** `stored` only when EVERY contributing row is the stored payroll row. */
  source: 'stored' | 'computed';
  /** How many punches contributed. */
  rows: number;
  /** The scheduled hours — the MAX over the rows, not a sum: a split shift's rows share one schedule. */
  scheduleMinutes: number;
  workedMinutes: number;
  /** Σ `late` — as the web Summary tab sums a date's rows. */
  lateMinutes: number;
  /** Σ `over` (overbreak). */
  overbreakMinutes: number;
  /** Σ `under` (undertime). */
  undertimeMinutes: number;
  /** Σ `reg_nd` (night differential). */
  nightDiffMinutes: number;
  /** Σ MEASURED overtime — what the engine recorded, before the review gate. */
  overtimeMinutes: number;
  /** Σ PAYABLE overtime — 0 for every row whose review is pending or rejected. */
  payableOvertimeMinutes: number;
  /**
   * The review state across the rows, worst first: `''` when ANY row is a
   * preview (no decision exists yet), else `pending` when any review is still
   * open, else `rejected` when any was refused, else `approved`.
   */
  otStatus: AttendanceOtReviewState;
  /** Overtime was measured and nobody has authorised it yet (a preview, or a review still open). */
  otAwaitingApproval: boolean;
}

const toMinutes = (hours: number): number => Math.round(hours * 60);

/**
 * Sum ERPat's figures over the day's punches that carry them. Null when none
 * does — the day is the device's to measure. A `rejected` row never
 * contributes ({@link countedSessions}).
 */
export function rollupDayMetrics(sessions: PunchSession[]): DayMetricsRollup | null {
  const rows = countedSessions(sessions).filter((s) => s.metrics != null);
  if (!rows.length) return null;

  const rollup: DayMetricsRollup = {
    source: rows.every((s) => s.metricsSource === 'stored') ? 'stored' : 'computed',
    rows: rows.length,
    scheduleMinutes: 0,
    workedMinutes: 0,
    lateMinutes: 0,
    overbreakMinutes: 0,
    undertimeMinutes: 0,
    nightDiffMinutes: 0,
    overtimeMinutes: 0,
    payableOvertimeMinutes: 0,
    otStatus: 'approved',
    otAwaitingApproval: false,
  };

  let preview = false;
  let pending = false;
  let rejected = false;
  for (const s of rows) {
    const m = s.metrics!;
    rollup.scheduleMinutes = Math.max(rollup.scheduleMinutes, toMinutes(m.schedule));
    rollup.workedMinutes += toMinutes(m.worked);
    rollup.lateMinutes += toMinutes(m.late);
    rollup.overbreakMinutes += toMinutes(m.over);
    rollup.undertimeMinutes += toMinutes(m.under);
    rollup.nightDiffMinutes += toMinutes(m.regNd);
    rollup.overtimeMinutes += toMinutes(m.measuredOt.total);
    rollup.payableOvertimeMinutes += toMinutes(m.payableOt.total);
    if (m.otStatus === '') preview = true;
    else if (m.otStatus === 'pending') pending = true;
    else if (m.otStatus === 'rejected') rejected = true;
  }

  rollup.otStatus = preview ? '' : pending ? 'pending' : rejected ? 'rejected' : 'approved';
  rollup.otAwaitingApproval = rollup.overtimeMinutes > 0 && (rollup.otStatus === '' || rollup.otStatus === 'pending');
  return rollup;
}

/**
 * The overtime figure's qualifier for a day with ERPat's figures — only when
 * overtime was actually measured, so a plain 0 never reads "not payable".
 */
function overtimeHint(rollup: DayMetricsRollup): string {
  if (rollup.overtimeMinutes <= 0) return '';
  if (rollup.source === 'computed' || rollup.otStatus === '') return 'awaiting approval';
  if (rollup.otStatus === 'pending') return 'under review';
  if (rollup.otStatus === 'rejected') return 'not payable';
  return '';
}

/**
 * Measure ONE day from its punch sessions and the shift it was scheduled
 * against.
 *
 * PRECEDENCE, per punch: a session that carries ERPat's `metrics` contributes
 * the engine's figures — worked, late, overbreak, undertime, night differential
 * and MEASURED overtime — and none of the device arithmetic runs on it. A
 * session with `metrics == null` (an open punch, or an older backend) is
 * measured here with the rules the Home widgets apply: today an open punch
 * accrues up to the current clock; on a past day it contributes nothing,
 * because a punch nobody ever closed is a missing clock-out, not an all-night
 * shift. The BREAK total is always the device's — the server reports only the
 * overbreak, never the break time itself. Each figure says which
 * ({@link DayFigureVM.source}).
 *
 * `isToday` decides how an OPEN punch (or an unended break) is counted.
 *
 * A `rejected` row is not attendance: it is dropped before anything below is
 * measured ({@link countedSessions}), so it neither opens the day nor adds a
 * minute to it.
 */
export function buildDayDetail(
  allSessions: PunchSession[],
  shift: DayShift,
  isToday: boolean,
  at: Date = new Date(),
): AttendanceDayDetailVM {
  const sessions = countedSessions(allSessions);
  const first = sessions[0];
  const last = sessions[sessions.length - 1];
  const hasPunch = sessions.length > 0;

  // ERPat's figures over the punches that carry them, and the punches left to
  // the device. A device-measured punch is always FILED under its own punch-in
  // date (`rowDayKey`: only a row with metrics can be filed elsewhere), so every
  // device minute below and the shift frame share one midnight.
  const rollup = rollupDayMetrics(sessions);
  const deviceSessions = sessions.filter((s) => s.metrics == null);
  const serverSource: FigureSource = rollup ? (deviceSessions.length ? 'mixed' : rollup.source) : 'device';

  // The shift's own frame. An overnight window (out at/before in) has its end
  // carried past midnight — every "past the scheduled end?" test below runs in
  // this frame, and so does `outAbsolute`.
  const overnight =
    shift.inMinutes != null && shift.outMinutes != null && shift.outMinutes <= shift.inMinutes;
  const shiftOut = shift.outMinutes != null && overnight ? shift.outMinutes + 24 * 60 : shift.outMinutes;

  // Each punch measured on its own, in SECONDS after its punch-in day's
  // midnight — an open one TODAY up to `at`, to the second. See measureSession.
  const measured = new Map(sessions.map((s) => [s, measureSession(s, isToday, at)]));
  const measure = (s: PunchSession): SessionSpan => measured.get(s)!;
  const tapSeconds = (s: PunchSession): number => measure(s).breaks.reduce((sum, [a, b]) => sum + (b - a), 0);

  // Worked: ERPat's hours for every punch it measured, plus the device's for
  // the rest — its time on the clock LESS its own breaks, so worked time stops
  // while a break runs (an open punch today accrues to now; a past open punch
  // adds nothing). Split-shift gaps lie between punches and were never in it.
  const deviceWorkedSeconds = deviceSessions.reduce((sum, s) => {
    const m = measure(s);
    return sum + Math.max(0, m.end - m.start - tapSeconds(s));
  }, 0);
  const workedSeconds = (rollup?.workedMinutes ?? 0) * 60 + deviceWorkedSeconds;
  const workedMinutes = Math.floor(workedSeconds / 60);

  // Break: ALWAYS the device — the server has no break total, only `over`.
  // Every punch's taps (a running break counted so far), plus the gaps of a
  // split shift.
  const breaks = collectBreaks(sessions);
  const gapSeconds = breaks
    .filter((b) => b.source === 'gap' && b.out != null && b.endsAt != null)
    .reduce((sum, b) => sum + Math.max(0, (b.endsAt! - b.out!) * 60), 0);
  const breakSeconds = sessions.reduce((sum, s) => sum + tapSeconds(s), 0) + gapSeconds;
  const breakMinutes = Math.floor(breakSeconds / 60);

  // Late: ERPat's `late` wherever the day has figures (grace and force-8h
  // applied) — on a SPLIT shift the sum over its rows, which is what the web
  // Summary tab and `/me/attendance/summary` total for the date (a single row's
  // sum is that row's own figure, so one-punch days read exactly as before);
  // else the device's: the FIRST punch-in past the scheduled start, floored at
  // 0 and capped at a half-day, beyond which the day reads as an absence, not
  // lateness.
  let lateMinutes = 0;
  let lateSource: FigureSource = 'device';
  if (rollup && sessions.length > 1) {
    lateMinutes = rollup.lateMinutes;
    lateSource = rollup.source;
  } else if (first?.metrics) {
    lateMinutes = toMinutes(first.metrics.late);
    lateSource = first.metricsSource === 'stored' ? 'stored' : 'computed';
  } else if (shift.isWorkday && shift.inMinutes != null && first) {
    lateMinutes = Math.min(Math.max(0, first.inMinutes - shift.inMinutes), MAX_TARDY_MINUTES_PER_DAY);
  }

  // Overtime: ERPat's MEASURED total (the review may still withhold it — see the
  // hint), plus the device's accrual only for a LAST punch that is still open
  // today: time on the clock past the scheduled end, in the shift's own frame so
  // an overnight shift measures against its next-morning end rather than
  // reporting the whole shift as overtime, and only once that end has passed. A
  // closed punch without metrics is measured the same way it always was. Only
  // the punch's OWN time past the end counts — a punch that started after the
  // scheduled end is measured from its clock-in, not from the end it was never
  // on the clock for, so the idle gap before it is not overtime. And only its
  // WORKING time: a break taken past the end is not overtime.
  let deviceOvertimeSeconds = 0;
  if (shift.isWorkday && shiftOut != null && last && last.metrics == null) {
    const m = measure(last);
    const from = Math.max(shiftOut * 60, m.start);
    if (m.end > from) deviceOvertimeSeconds = Math.max(0, m.end - from - overlapSeconds(m.breaks, from, m.end));
  }
  const deviceOvertimeMinutes = Math.floor(deviceOvertimeSeconds / 60);
  const overtimeSeconds = (rollup?.overtimeMinutes ?? 0) * 60 + deviceOvertimeSeconds;
  const overtimeMinutes = Math.floor(overtimeSeconds / 60);
  const overtimeSource: FigureSource = rollup
    ? deviceOvertimeMinutes > 0
      ? 'mixed'
      : rollup.source
    : 'device';
  const otHint = rollup ? overtimeHint(rollup) : '';

  // A clock-out the server left more than a day past its punch-in: the day's
  // span cannot be measured off it, so say so instead of printing a figure. Only
  // a device-measured punch — ERPat's figures for a row already read it.
  const punchOutUnresolved = deviceSessions.some((s) => s.outMinutes != null && s.outAbsolute == null);

  // Overbreak: ERPat's `over` when the day has figures (the tenant's real
  // allowance), else the device's break total past NORMAL_BREAK_MINUTES — and
  // only on a day that was actually worked.
  const overBreakMinutes = rollup
    ? rollup.overbreakMinutes
    : hasPunch
      ? Math.max(0, breakMinutes - NORMAL_BREAK_MINUTES)
      : 0;
  const overBreakSource: FigureSource = rollup ? rollup.source : 'device';

  const figures: DayFigureVM[] = [
    {
      key: 'worked',
      label: 'Work',
      value: punchOutUnresolved ? '—' : compactDur(workedMinutes),
      minutes: workedMinutes,
      warn: false,
      source: serverSource,
      hint: '',
    },
    {
      key: 'late',
      label: 'Late',
      value: compactDur(lateMinutes),
      minutes: lateMinutes,
      warn: lateMinutes > 0,
      source: lateSource,
      hint: '',
    },
    {
      key: 'overtime',
      label: 'Overtime',
      value: compactDur(overtimeMinutes),
      minutes: overtimeMinutes,
      warn: false,
      source: overtimeSource,
      hint: otHint,
    },
    {
      key: 'break',
      label: 'Break',
      value: compactDur(breakMinutes),
      minutes: breakMinutes,
      warn: false,
      source: 'device',
      hint: '',
    },
    {
      key: 'overbreak',
      label: 'Over Break',
      value: compactDur(overBreakMinutes),
      minutes: overBreakMinutes,
      warn: overBreakMinutes > 0,
      source: overBreakSource,
      hint: '',
    },
  ];
  // Night differential exists only as ERPat's figure — the device never
  // measures it — so the tile appears only on a day with a rollup.
  if (rollup) {
    figures.push({
      key: 'nightdiff',
      label: 'Night diff.',
      value: compactDur(rollup.nightDiffMinutes),
      minutes: rollup.nightDiffMinutes,
      warn: false,
      source: rollup.source,
      hint: '',
    });
  }

  const shiftLabel =
    shift.isWorkday && shift.inMinutes != null && shift.outMinutes != null
      ? `${minutesToLabel(shift.inMinutes)} – ${minutesToLabel(shift.outMinutes)}`
      : '';

  // Relative to the day the punches are FILED under, not the punch-in date: a
  // night shift ERPat logs on the morning it ends "started the previous day",
  // one it logs on the evening it starts "ended the next day".
  const filedKey = last?.dateKey ?? '';
  const outGap = last && last.outDateKey ? dayGap(filedKey, last.outDateKey) : null;
  const inGap = first ? dayGap(first.dateKey, first.inDateKey) : null;

  return {
    hasPunch,
    timeInLabel: first ? minutesToLabel(first.inMinutes) : '',
    // The REAL clock time, read off `outAbsolute` (which `minutesToLabel` wraps
    // back under 24h), so an overnight shift shows "5:40 AM" and not the
    // midnight the same-day model clamps `outMinutes` to.
    timeOutLabel: last?.outAbsolute != null ? minutesToLabel(last.outAbsolute) : '',
    // Exactly one day either way — a clock-out further out is a stuck record
    // (`punchOutUnresolved`), not a shift that "ended the next day".
    endedNextDay: outGap === 1,
    startedPreviousDay: inGap === -1,
    stillClockedIn: last != null && last.outMinutes == null,
    shiftLabel,
    sessionCount: sessions.length,
    metricsSource: serverSource,
    logDate: rollup ? (sessions.find((s) => s.metrics != null)?.dateKey ?? '') : '',
    otHint,
    figures,
    breaks: breaks.map((b) => ({
      outLabel: b.out != null ? minutesToLabel(b.out) : '',
      inLabel: b.in != null ? minutesToLabel(b.in) : '',
      durationLabel: b.out != null && b.endsAt != null ? compactDur(b.endsAt - b.out) : '',
      open: b.endsAt == null,
      source: b.source,
    })),
    notes: sessions.map((s) => s.note).filter((n) => n !== ''),
    punchOutUnresolved,
    liveState: liveStateOf(last, isToday),
    seconds: { worked: workedSeconds, break: breakSeconds, overtime: overtimeSeconds },
  };
}

/**
 * What the day's shift is doing right now: null unless it is TODAY and the
 * last punch is still open; `on_break` while that punch's break taps are odd
 * in number — the same parity the clock buttons read.
 */
function liveStateOf(last: PunchSession | undefined, isToday: boolean): DayLiveState | null {
  if (!isToday || !last || last.outMinutes != null || last.outAbsolute != null) return null;
  return last.breaks.length % 2 === 1 ? 'on_break' : 'working';
}

/**
 * What a day's shift is doing right now, without measuring the day — the same
 * answer as {@link AttendanceDayDetailVM.liveState}. It depends only on the
 * punches, never on the clock, so a timer can ask whether to tick at all.
 */
export function dayLiveState(sessions: PunchSession[], isToday: boolean): DayLiveState | null {
  const counted = countedSessions(sessions);
  return liveStateOf(counted[counted.length - 1], isToday);
}

/** One punch as the device measures it, in SECONDS after its punch-in day's midnight. */
interface SessionSpan {
  /** Where the punch starts and stops accruing — `start === end` when it measures nothing. */
  start: number;
  end: number;
  /** Its break taps as `[out, back]` spans, clamped to the punch and merged, chronological. */
  breaks: Array<[number, number]>;
}

/**
 * "Now" in a punch's frame: seconds after the midnight of `dateKey` — so a
 * clock past midnight reads past 24h for a punch begun the evening before.
 *
 * Counted off the CALENDAR, never off "the clock reads earlier than the
 * punch-in": at second precision a device clock running one second behind the
 * server's clock-in stamp would read as a day later — 23:59:59 worked the
 * moment after a Time In.
 */
export function nowInFrame(dateKey: string, at: Date): number {
  const days = dayGap(dateKey, localDateKey(at)) ?? 0;
  return days * 86_400 + secondsOfDay(at);
}

/**
 * One punch, measured on its own. A closed punch ends at its real clock-out;
 * an open one accrues to `at` TODAY and counts nothing on a past day, because
 * a punch nobody closed is a missing clock-out, not an all-night shift. So is a
 * clock-out the server left more than a day out (`outAbsolute` null with an out
 * stamp present) — see the field's docblock.
 *
 * PRECISION. An OPEN punch is measured to the second — its punch-in, its taps
 * and `at` — so a running timer loses nothing to the minute floor and does not
 * jump when a break ends. A CLOSED one is measured in whole minutes, exactly as
 * it always was, so a finished day reads the same everywhere.
 *
 * Breaks are the punch's tap pairs. A return tap the server sent unreadable
 * ends that break at the next break-out (it can only have come before it);
 * with neither, the break runs to where the punch stops accruing. Spans are
 * clamped to the punch and merged, so malformed or out-of-order taps are never
 * counted twice.
 */
function measureSession(s: PunchSession, isToday: boolean, at: Date): SessionSpan {
  const open = s.outMinutes == null && s.outAbsolute == null;
  let start: number;
  let end: number;
  let taps: (number | null)[];
  if (open) {
    start = s.inSeconds;
    end = isToday ? Math.max(start, nowInFrame(s.inDateKey, at)) : start;
    taps = s.breakSeconds;
  } else {
    start = s.inMinutes * 60;
    end = s.outAbsolute != null ? Math.max(start, s.outAbsolute * 60) : start;
    taps = s.breaks.map((t) => (t == null ? null : t * 60));
  }

  const spans: Array<[number, number]> = [];
  for (let i = 0; i < taps.length; i += 2) {
    const out = taps[i];
    if (out == null) continue;
    const back = taps[i + 1] ?? taps[i + 2] ?? null;
    const from = Math.min(Math.max(out, start), end);
    const to = Math.min(Math.max(back ?? end, start), end);
    if (to > from) spans.push([from, to]);
  }
  return { start, end, breaks: mergeSpans(spans) };
}

/** Sort and merge overlapping `[from, to]` spans. */
function mergeSpans(spans: Array<[number, number]>): Array<[number, number]> {
  const merged: Array<[number, number]> = [];
  for (const [from, to] of [...spans].sort((a, b) => a[0] - b[0])) {
    const prev = merged[merged.length - 1];
    if (prev && from <= prev[1]) prev[1] = Math.max(prev[1], to);
    else merged.push([from, to]);
  }
  return merged;
}

/** Seconds of merged `spans` that fall inside `[from, to]`. */
function overlapSeconds(spans: Array<[number, number]>, from: number, to: number): number {
  return spans.reduce((sum, [a, b]) => sum + Math.max(0, Math.min(b, to) - Math.max(a, from)), 0);
}

/**
 * The one-line provenance a "today" surface prints under its figures — which
 * source measured the day, in the employee's own terms:
 *   - while a punch is still OPEN, this device is measuring it, whatever the
 *     closed rows beside it carry: the day is not settled until clock-out;
 *   - once clocked out, read off each figure's own provenance
 *     ({@link DayFigureVM.source}) — the break total is always the device's and
 *     says nothing about the rest: any `computed` figure means ERPat's live run,
 *     finalised on approval; every figure `stored` means the approved payroll
 *     row; ERPat's beside a device-measured one is named as such; and nothing of
 *     ERPat's at all (an older backend) is the device's measurement.
 * '' when the day has no punch — there is nothing to attribute.
 */
export function todayFiguresNote(detail: AttendanceDayDetailVM): string {
  if (!detail.hasPunch) return '';
  if (detail.stillClockedIn) return 'Measured on this device until you clock out';
  const sources = detail.figures.filter((f) => f.key !== 'break').map((f) => f.source);
  if (sources.includes('computed')) return "ERPat's figures for today — live, finalised on approval";
  if (sources.length > 0 && sources.every((s) => s === 'stored')) return "ERPat's figures for today";
  if (sources.includes('stored')) return "ERPat's figures for today, part measured on this device";
  return 'Measured on this device from your punches';
}
